/*
Copyright 2026 AB Civil Consulting

Simplified Screen Share Annotation - Slack Huddle Style
- Pen drawing only
- Auto-vanishing strokes (3 seconds) with gradual fade
- Fixed per-user colors
- User initials labels

COORDINATE FIX: Accounts for object-fit: contain letterboxing
*/

import {
  type FC,
  useRef,
  useCallback,
  useState,
  useEffect,
  type PointerEvent,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { logger as rootLogger } from "matrix-js-sdk/lib/logger";

import styles from "./AnnotationCanvas.module.css";
import { useAnnotation } from "./AnnotationContext";
import type { Stroke, Point } from "./types";
import { setCaptureFrameInfo, notifyScreenShareStarted, showOverlay } from "./electronOverlay";

const logger = rootLogger.getChild("[AnnotationOverlay]");

// 10 distinct colors for users
const USER_COLORS = [
  "#e91e63", // Pink
  "#9c27b0", // Purple
  "#3f51b5", // Indigo
  "#2196f3", // Blue
  "#00bcd4", // Cyan
  "#009688", // Teal
  "#4caf50", // Green
  "#ff9800", // Orange
  "#ff5722", // Deep Orange
  "#795548", // Brown
];

const SOLID_DURATION = 3000; // 3 seconds fully visible
const FADE_DURATION = 3000;  // 3 seconds to fade out
const TOTAL_DURATION = SOLID_DURATION + FADE_DURATION; // 6 seconds total
const STROKE_SIZE = 4;

// Get consistent color for a user based on their ID
export const getUserColor = (userId: string): string => {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = ((hash << 5) - hash) + userId.charCodeAt(i);
    hash = hash & hash;
  }
  return USER_COLORS[Math.abs(hash) % USER_COLORS.length];
};

// Get initials from user ID or name
const getInitials = (userId: string, userName?: string): string => {
  if (userName) {
    const parts = userName.split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return userName.slice(0, 2).toUpperCase();
  }
  const match = userId.match(/@([^:]+)/);
  if (match) return match[1].slice(0, 2).toUpperCase();
  return userId.slice(0, 2).toUpperCase();
};

/**
 * Calculate the actual video content bounds within a container
 * when using object-fit: contain.
 *
 * Returns the offset and scale to map container coordinates to video coordinates.
 */
interface VideoContentBounds {
  offsetX: number;      // Left offset of video content within container
  offsetY: number;      // Top offset of video content within container
  contentWidth: number; // Actual width of video content
  contentHeight: number; // Actual height of video content
  scale: number;        // Scale factor from video intrinsic to display
}

function getVideoContentBounds(video: HTMLVideoElement): VideoContentBounds | null {
  const containerWidth = video.offsetWidth;
  const containerHeight = video.offsetHeight;
  const videoWidth = video.videoWidth;
  const videoHeight = video.videoHeight;

  if (!containerWidth || !containerHeight || !videoWidth || !videoHeight) {
    return null;
  }

  const containerAspect = containerWidth / containerHeight;
  const videoAspect = videoWidth / videoHeight;

  let contentWidth: number;
  let contentHeight: number;
  let offsetX: number;
  let offsetY: number;
  let scale: number;

  if (videoAspect > containerAspect) {
    // Video is wider - letterbox top/bottom
    contentWidth = containerWidth;
    scale = containerWidth / videoWidth;
    contentHeight = videoHeight * scale;
    offsetX = 0;
    offsetY = (containerHeight - contentHeight) / 2;
  } else {
    // Video is taller - letterbox left/right
    contentHeight = containerHeight;
    scale = containerHeight / videoHeight;
    contentWidth = videoWidth * scale;
    offsetX = (containerWidth - contentWidth) / 2;
    offsetY = 0;
  }

  return { offsetX, offsetY, contentWidth, contentHeight, scale };
}

/**
 * Transform container coordinates to normalized video coordinates (0-1).
 * Returns null if the point is outside the video content area (in letterbox).
 */
function containerToNormalized(
  containerX: number,
  containerY: number,
  bounds: VideoContentBounds
): { x: number; y: number } | null {
  // Check if point is within video content area
  if (
    containerX < bounds.offsetX ||
    containerX > bounds.offsetX + bounds.contentWidth ||
    containerY < bounds.offsetY ||
    containerY > bounds.offsetY + bounds.contentHeight
  ) {
    // Point is in letterbox area - clamp to edge instead of rejecting
    const clampedX = Math.max(bounds.offsetX, Math.min(containerX, bounds.offsetX + bounds.contentWidth));
    const clampedY = Math.max(bounds.offsetY, Math.min(containerY, bounds.offsetY + bounds.contentHeight));

    return {
      x: (clampedX - bounds.offsetX) / bounds.contentWidth,
      y: (clampedY - bounds.offsetY) / bounds.contentHeight,
    };
  }

  return {
    x: (containerX - bounds.offsetX) / bounds.contentWidth,
    y: (containerY - bounds.offsetY) / bounds.contentHeight,
  };
}

/**
 * Transform normalized video coordinates (0-1) to container coordinates.
 */
function normalizedToContainer(
  normX: number,
  normY: number,
  bounds: VideoContentBounds
): { x: number; y: number } {
  return {
    x: bounds.offsetX + normX * bounds.contentWidth,
    y: bounds.offsetY + normY * bounds.contentHeight,
  };
}

interface PendingStroke {
  id: string;
  points: Point[];
  color: string;
  size: number;
  userId: string;
  userName?: string;
  trackId?: string;
  sessionId?: string;
}

interface AnnotationOverlayProps {
  userId: string;
  userName?: string;
  isSharer: boolean;
  trackId?: string;
  sessionId?: string;
  onClose?: () => void;
}

export const AnnotationOverlay: FC<AnnotationOverlayProps> = ({
  userId,
  userName,
  trackId,
  sessionId,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoBoundsRef = useRef<VideoContentBounds | null>(null);

  const {
    annotations,
    pendingStrokes,
    addStroke,
    sendPoints,
    clearAnnotations,
    undoAnnotation,
    isConnected,
  } = useAnnotation();

  const [drawingEnabled, setDrawingEnabled] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentStroke, setCurrentStroke] = useState<Stroke | null>(null);

  // REFS for animation loop - these are updated when state changes
  // but the animation loop reads from refs to avoid restarts
  const annotationsRef = useRef<Stroke[]>([]);
  const pendingStrokesRef = useRef<Map<string, PendingStroke>>(new Map());
  const currentStrokeRef = useRef<Stroke | null>(null);

  // Keep refs in sync with state
  useEffect(() => {
    annotationsRef.current = annotations;
  }, [annotations]);

  useEffect(() => {
    pendingStrokesRef.current = pendingStrokes;
  }, [pendingStrokes]);

  useEffect(() => {
    currentStrokeRef.current = currentStroke;
  }, [currentStroke]);
  // Find and track the video element - AGGRESSIVE VERSION
  useEffect(() => {
    logger.info(" useEffect mounting - looking for video...");
    
    const findVideo = (): HTMLVideoElement | null => {
      // Method 1: Search from container up the tree
      if (containerRef.current) {
        let searchRoot: Element | null = containerRef.current;
        for (let i = 0; i < 5 && searchRoot?.parentElement; i++) {
          searchRoot = searchRoot.parentElement;
        }
        if (searchRoot) {
          const v = searchRoot.querySelector("video") as HTMLVideoElement | null;
          if (v) {
            logger.info(" Found video via tree traversal");
            return v;
          }
        }
      }

      // Method 2: querySelector with data-testid
      const v2 = document.querySelector("video[data-testid='video']") as HTMLVideoElement | null;
      if (v2) {
        logger.info(" Found video via data-testid");
        return v2;
      }

      // Method 3: Any video element
      const v3 = document.querySelector("video") as HTMLVideoElement | null;
      if (v3) {
        logger.info(" Found video via generic selector");
        return v3;
      }

      // Method 4: Search all iframes
      const iframes = document.querySelectorAll("iframe");
      for (const iframe of iframes) {
        try {
          const v = iframe.contentDocument?.querySelector("video") as HTMLVideoElement | null;
          if (v) {
            logger.info(" Found video in iframe");
            return v;
          }
        } catch (e) {
          // Cross-origin iframe, can't access
        }
      }

      logger.info(" No video found");
      return null;
    };

    // Track if we've sent dimensions
    let dimensionsSent = false;

    const checkAndSendDimensions = (): void => {
      if (dimensionsSent) return;

      const video = findVideo();
      if (video) {
        videoRef.current = video;
        const vw = video.videoWidth;
        const vh = video.videoHeight;
        const cw = video.offsetWidth;
        const ch = video.offsetHeight;
        
        logger.info(" Video found:", {
          videoWidth: vw,
          videoHeight: vh,
          offsetWidth: cw,
          offsetHeight: ch,
        });

        if (vw && vh) {
          logger.info(" SENDING dimensions to Electron:", vw, "x", vh);
          setCaptureFrameInfo(vw, vh);
          notifyScreenShareStarted({ frameWidth: vw, frameHeight: vh });
          showOverlay();
          dimensionsSent = true;
        } else {
          logger.info(" Video exists but dimensions are 0, waiting...");
        }

        // Also update bounds for rendering
        const bounds = getVideoContentBounds(video);
        if (bounds) {
          videoBoundsRef.current = bounds;
        }
      }
    };

    // Check immediately
    checkAndSendDimensions();

    // Keep checking until we send dimensions or timeout
    const interval = setInterval(() => {
      if (!dimensionsSent) {
        checkAndSendDimensions();
      }
    }, 200);

    // Also listen for video loadedmetadata event
    const handleMetadata = (): void => {
      logger.info(" Video loadedmetadata event fired");
      checkAndSendDimensions();
    };

    // Try to attach listener if video exists
    const video = findVideo();
    if (video) {
      video.addEventListener("loadedmetadata", handleMetadata);
    }

    return () => {
      clearInterval(interval);
      if (video) {
        video.removeEventListener("loadedmetadata", handleMetadata);
      }
    };
  }, []);

  // Point batching for streaming
  const pointBatchRef = useRef<Point[]>([]);
  const lastSendTimeRef = useRef(0);
  const currentStrokeIdRef = useRef<string | null>(null);
  const POINT_BATCH_INTERVAL = 50;

  // User's assigned color (fixed, based on userId)
  const myColor = getUserColor(userId);

  // Get canvas point (normalized 0-1) - FIXED for letterboxing
  const getPoint = useCallback((e: PointerEvent<HTMLCanvasElement>): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0, t: Date.now() };

    const rect = canvas.getBoundingClientRect();
    const containerX = e.clientX - rect.left;
    const containerY = e.clientY - rect.top;

    // If we have video bounds, normalize to video content area
    const video = videoRef.current;
    if (video) {
      const bounds = getVideoContentBounds(video);
      if (bounds) {
        videoBoundsRef.current = bounds;
        const normalized = containerToNormalized(containerX, containerY, bounds);
        if (normalized) {
          return {
            x: normalized.x,
            y: normalized.y,
            t: Date.now(),
          };
        }
      }
    }

    // Fallback: normalize to container (old behavior)
    return {
      x: containerX / rect.width,
      y: containerY / rect.height,
      t: Date.now(),
    };
  }, []);

  // Flush point batch
  const flushPointBatch = useCallback(() => {
    if (pointBatchRef.current.length > 0 && currentStrokeIdRef.current) {
      void sendPoints(
        currentStrokeIdRef.current,
        pointBatchRef.current,
        myColor,      // Include sender's color
        STROKE_SIZE,  // Include sender's size
        trackId,
        sessionId,
        "vanishing"
      );
      pointBatchRef.current = [];
      lastSendTimeRef.current = Date.now();
    }
  }, [sendPoints, myColor, trackId, sessionId]);

  // Handle pointer down - start drawing
  const handlePointerDown = useCallback((e: PointerEvent<HTMLCanvasElement>) => {
    if (!drawingEnabled || !isConnected) return;

    e.preventDefault();
    e.stopPropagation();

    const point = getPoint(e);
    const strokeId = uuidv4();

    const newStroke: Stroke = {
      id: strokeId,
      points: [point],
      color: myColor,
      size: STROKE_SIZE,
      userId,
      userName,
      trackId,
      sessionId,
      createdAt: Date.now(),
      tool: "vanishing",
    };

    logger.info("[AnnotationOverlay] Starting stroke:", strokeId, "color:", myColor, "point:", point);
    setCurrentStroke(newStroke);
    setIsDrawing(true);
    currentStrokeIdRef.current = strokeId;
    pointBatchRef.current = [point];
    lastSendTimeRef.current = Date.now();
    void sendPoints(strokeId, [point], myColor, STROKE_SIZE, trackId, sessionId, "vanishing");

    try {
      (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
    } catch {
      // Ignore capture errors
    }
  }, [drawingEnabled, isConnected, getPoint, myColor, userId, userName, trackId, sessionId, sendPoints]);

  // Handle pointer move - add points
  const handlePointerMove = useCallback((e: PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !currentStroke) return;

    e.preventDefault();
    const point = getPoint(e);

    setCurrentStroke(prev => prev ? {
      ...prev,
      points: [...prev.points, point],
    } : null);

    pointBatchRef.current.push(point);
    if (Date.now() - lastSendTimeRef.current >= POINT_BATCH_INTERVAL) {
      flushPointBatch();
    }
  }, [isDrawing, currentStroke, getPoint, flushPointBatch]);

  // Handle pointer up - finish stroke
  const handlePointerUp = useCallback(async (e: PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;

    setIsDrawing(false);

    try {
      (e.target as HTMLCanvasElement).releasePointerCapture(e.pointerId);
    } catch {
      // Ignore release errors
    }

    flushPointBatch();
    if (currentStroke && currentStroke.points.length > 1) {
      logger.info("[AnnotationOverlay] Finishing stroke:", currentStroke.id);
      await addStroke(currentStroke);
    }
    setCurrentStroke(null);
    currentStrokeIdRef.current = null;
    pointBatchRef.current = [];
  }, [isDrawing, currentStroke, flushPointBatch, addStroke]);

  // CONTINUOUS animation loop - runs once and reads from refs
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let animationFrameId: number;
    let running = true;

    const drawUserLabel = (
      ctx: CanvasRenderingContext2D,
      x: number,
      y: number,
      uId: string,
      uName: string | undefined,
      color: string,
      opacity: number
    ): void => {
      const initials = getInitials(uId, uName);
      const labelWidth = 28;
      const labelHeight = 18;

      ctx.globalAlpha = opacity;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.roundRect(x - labelWidth / 2, y - labelHeight - 8, labelWidth, labelHeight, 4);
      ctx.fill();

      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 10px sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(initials, x, y - labelHeight / 2 - 8);
      ctx.globalAlpha = 1;
    };

    const render = (): void => {
      if (!running) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        animationFrameId = requestAnimationFrame(render);
        return;
      }

      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        animationFrameId = requestAnimationFrame(render);
        return;
      }

      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, rect.width, rect.height);

      const now = Date.now();
      const drawnUserLabels = new Set<string>();

      // Get video bounds for rendering
      const video = videoRef.current;
      const bounds = video ? getVideoContentBounds(video) : null;

      // Helper to convert normalized coords to canvas coords
      const toCanvas = (normX: number, normY: number): { x: number; y: number } => {
        if (bounds) {
          return normalizedToContainer(normX, normY, bounds);
        }
        // Fallback: direct mapping to canvas size
        return { x: normX * rect.width, y: normY * rect.height };
      };

      // Read from refs - not state
      const currentAnnotations = annotationsRef.current;
      const currentPendingStrokes = pendingStrokesRef.current;
      const localCurrentStroke = currentStrokeRef.current;

      // Draw all annotations with fade effect
      for (const annotation of currentAnnotations) {
        if (!annotation.points || annotation.points.length < 2) continue;

        const age = now - annotation.createdAt;
        let opacity: number;
        if (age < SOLID_DURATION) {
          opacity = 1.0;
        } else if (age < TOTAL_DURATION) {
          opacity = 1 - (age - SOLID_DURATION) / FADE_DURATION;
        } else {
          opacity = 0;
        }

        if (opacity <= 0) continue;

        ctx.globalAlpha = opacity;
        ctx.beginPath();
        ctx.strokeStyle = annotation.color;
        ctx.lineWidth = annotation.size;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";

        const [first, ...rest] = annotation.points;
        const p0 = toCanvas(first.x, first.y);
        ctx.moveTo(p0.x, p0.y);
        for (const point of rest) {
          const p = toCanvas(point.x, point.y);
          ctx.lineTo(p.x, p.y);
        }
        ctx.stroke();

        // Draw user label at first point
        if (!drawnUserLabels.has(annotation.userId)) {
          drawnUserLabels.add(annotation.userId);
          drawUserLabel(ctx, p0.x, p0.y, annotation.userId, annotation.userName, annotation.color, opacity);
        }

        ctx.globalAlpha = 1;
      }

      // Draw pending strokes (from remote users currently drawing)
      for (const stroke of Array.from(currentPendingStrokes.values())) {
        if (!stroke.points || stroke.points.length < 2) continue;

        ctx.globalAlpha = 0.8;
        ctx.beginPath();
        ctx.strokeStyle = stroke.color;
        ctx.lineWidth = stroke.size || STROKE_SIZE;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";

        const [first, ...rest] = stroke.points;
        const p0 = toCanvas(first.x, first.y);
        ctx.moveTo(p0.x, p0.y);
        for (const point of rest) {
          const p = toCanvas(point.x, point.y);
          ctx.lineTo(p.x, p.y);
        }
        ctx.stroke();

        if (!drawnUserLabels.has(stroke.userId)) {
          drawnUserLabels.add(stroke.userId);
          drawUserLabel(ctx, p0.x, p0.y, stroke.userId, stroke.userName, stroke.color, 0.8);
        }

        ctx.globalAlpha = 1;
      }

      // Draw current stroke (local user drawing)
      if (localCurrentStroke && localCurrentStroke.points && localCurrentStroke.points.length >= 2) {
        ctx.globalAlpha = 1;
        ctx.beginPath();
        ctx.strokeStyle = localCurrentStroke.color;
        ctx.lineWidth = localCurrentStroke.size;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";

        const [first, ...rest] = localCurrentStroke.points;
        const p0 = toCanvas(first.x, first.y);
        ctx.moveTo(p0.x, p0.y);
        for (const point of rest) {
          const p = toCanvas(point.x, point.y);
          ctx.lineTo(p.x, p.y);
        }
        ctx.stroke();

        if (!drawnUserLabels.has(localCurrentStroke.userId)) {
          drawUserLabel(ctx, p0.x, p0.y, localCurrentStroke.userId, localCurrentStroke.userName, localCurrentStroke.color, 1);
        }
      }

      // Continue animation loop
      animationFrameId = requestAnimationFrame(render);
    };

    // Start the animation loop
    animationFrameId = requestAnimationFrame(render);

    return () => {
      running = false;
      cancelAnimationFrame(animationFrameId);
    };
  }, []); // Empty deps - animation loop runs continuously

  const canDraw = isConnected && drawingEnabled;

  return (
    <div
      ref={containerRef}
      className={styles.container}
      style={{ pointerEvents: canDraw ? "auto" : "none" }}
    >
      <canvas
        ref={canvasRef}
        className={styles.canvas}
        style={{ cursor: canDraw ? "crosshair" : "default" }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      />

      {/* Simple Toolbar */}
      <div className={styles.toolbar} style={{ pointerEvents: "auto" }}>
        {/* Connection indicator */}
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: isConnected ? "#22c55e" : "#ef4444",
            marginRight: 8,
          }}
          title={isConnected ? "Connected" : "Not connected"}
        />

        {/* Toggle drawing ON/OFF */}
        <button
          className={styles.toolButton}
          onClick={() => {
            if (!isConnected) return;
            setDrawingEnabled(!drawingEnabled);
          }}
          style={{
            background: drawingEnabled ? "rgba(34, 197, 94, 0.3)" : "rgba(255, 255, 255, 0.1)",
            border: drawingEnabled ? "2px solid #22c55e" : "2px solid transparent",
            fontWeight: "bold",
            minWidth: 40,
          }}
          title={drawingEnabled ? "Disable Drawing" : "Enable Drawing"}
        >
          {drawingEnabled ? "ON" : "OFF"}
        </button>

        {/* Show user's color indicator when drawing is enabled */}
        {drawingEnabled && (
          <>
            <div className={styles.divider} />

            {/* Color indicator */}
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: 4,
                backgroundColor: myColor,
                border: "2px solid white",
              }}
              title={`Your color: ${myColor}`}
            />

            <div className={styles.divider} />

            {/* Undo */}
            <button
              className={styles.toolButton}
              onClick={() => undoAnnotation(userId, trackId)}
              title="Undo"
            >
              ↶
            </button>

            {/* Clear my annotations */}
            <button
              className={styles.toolButton}
              onClick={() => clearAnnotations(userId, trackId, sessionId)}
              title="Clear My Annotations"
            >
              ✕
            </button>
          </>
        )}
      </div>
    </div>
  );
};

AnnotationOverlay.displayName = "AnnotationOverlay";
